<template>
  <v-dialog v-model="dialog" lazy persistent fullscreen>
    <v-container nowrap>
      <v-card class="px-4">
        <v-card-title class="pb-0 headline font-weight-bold"
          >{{ uc_type }} de Unidade Consumidora</v-card-title
        >

        <v-card-text>
          <v-form ref="consumer_unit_form" v-model="valid" lazy-validation>
            <v-container grid-list-md class="pa-0">
              <v-layout wrap>
                <v-flex xs12 sm6 md2>
                  <v-select
                    :items="person_type_items"
                    item-value="value"
                    item-text="text"
                    label="Entidade"
                    v-model="consumer_unit.person_type"
                    :rules="[(v) => !!v || 'Por favor, informe a entidade.']"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-select
                    label="Concessionária"
                    :items="utilityCompanies"
                    item-value="id"
                    item-text="name"
                    v-model="consumer_unit.utility_company"
                    :rules="[
                      (v) => !!v || 'Por favor, informe a concessionária.',
                    ]"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                    @change="resetCustomerNumberErrors"
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-text-field
                    v-model="consumer_unit.cpf"
                    :label="consumer_unit.person_type === 'pf' ? 'CPF' : 'CNPJ'"
                    :mask="
                      consumer_unit.person_type === 'pf'
                        ? '###.###.###-##'
                        : '##.###.###/####-##'
                    "
                    :rules="
                      consumer_unit.person_type === 'pf'
                        ? [
                            (v) =>
                              v.length >= 11 ||
                              'Por favor, preencha o CPF Completo.',
                            (v) =>
                              this.validaCPF(consumer_unit.cpf) === true ||
                              'CPF inválido.',
                          ]
                        : [
                            (v) =>
                              v.length >= 14 ||
                              'Por favor, preencha o CNPJ Completo.',
                            (v) =>
                              this.validaCNPJ(consumer_unit.cpf) === true ||
                              'CNPJ inválido.',
                          ]
                    "
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md3>
                  <v-select
                    label="Plano da UC"
                    :items="plans"
                    item-value="id"
                    :item-text="formattedPlanText"
                    return-object
                    v-model="plan"
                    :rules="[
                      (v) => !!v || 'Por favor, informe o plano do membro',
                    ]"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6 md3>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        @click="pickIdPhoto"
                        color="primary"
                        block
                      >
                        <v-progress-circular
                          v-if="loading_parse_bill"
                          color="white"
                          indeterminate
                          size="16"
                          class="mr-2"
                        ></v-progress-circular>
                        <v-icon v-else left aria-hidden="true"
                          >add_a_photo</v-icon
                        >
                        {{
                          idPhoto[idPhoto.length - 1].name
                            ? idPhoto[idPhoto.length - 1].name
                            : "ADICIONAR CONTA DE ENERGIA"
                        }}
                      </v-btn>
                    </template>
                    <span
                      >Apenas PDFs da 2º via de conta original emitidos pela
                      concessionária são permitidos.</span
                    >
                  </v-tooltip>
                  <input
                    type="file"
                    ref="idPhoto"
                    multiple
                    style="display: none"
                    @change="pickedPhotos($event)"
                    accept="application/pdf"
                    :disabled="!canEdit(consumer_unit.status)"
                    required
                    :rules="[
                      (v) => !!v || 'Por favor, adicione a conta de luz da UC.',
                    ]"
                  />
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-text-field
                    :rules="nameRules"
                    v-model="consumer_unit.name"
                    label="Nome do Titular"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                    @input="convertToTitleCase"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-text-field
                    ref="customerNumberField"
                    :rules="customerNumberRule"
                    v-model="consumer_unit.customer_number"
                    :label="customer_number_formatted"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                    type="number"
                    @keydown="preventNumberChange"
                  >
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            color="purple"
                            size="16"
                            v-on="on"
                            icon
                            style="cursor: default"
                            class="mr-1"
                            >mdi-alert-circle-outline</v-icon
                          >
                        </template>
                        <div>
                          <p class="font-weight-bold">
                            Atenção! O preenchimento incorreto deste campo pode
                            causar problemas na alocação do cliente.
                          </p>
                        </div>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-text-field
                    label="Número de Instalação"
                    v-model="consumer_unit.installation_number"
                    type="number"
                    :rules="installationNumberRule"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                    @keydown="preventNumberChange"
                  >
                    <template v-slot:append>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            color="purple"
                            size="16"
                            v-on="on"
                            icon
                            style="cursor: default"
                            class="icon_position"
                            >mdi-alert-circle-outline</v-icon
                          >
                        </template>
                        <div>
                          <p class="font-weight-bold">
                            Atenção! O preenchimento incorreto deste campo pode
                            causar problemas na alocação do cliente.
                          </p>
                        </div>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-combobox
                    :items="rate_classes"
                    v-model="consumer_unit.rate_class"
                    label="Classe"
                    required
                    :rules="[
                      (v) =>
                        !!v || 'Por favor, selecione a classe da instalação.',
                    ]"
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-combobox>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-combobox
                    v-model="consumer_unit.sub_class"
                    label="Sub Classe"
                    required
                    :items="subclasses"
                    item-text="subclass"
                    item-value="subclass"
                    :rules="[
                      (v) =>
                        !!v ||
                        'Por favor, selecione a sub classe da instalação!',
                    ]"
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-combobox>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-text-field
                    id="tarifa"
                    v-model.number="consumer_unit.fare_amount"
                    label="Tarifa Base"
                    type="number"
                    prefix="R$"
                    :rules="[
                      (v) => !!v || 'Por favor, determine a tarifa base.',
                    ]"
                    :hint="
                      consumer_unit.fare_amount < 0.7
                        ? 'ATENÇÃO! Tarifa menor que 0,70 apenas com aprovação da diretoria.'
                        : ''
                    "
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                    @keydown="preventNumberChange"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm12 md12>
                  <div class="font-weight-bold">
                    Endereço da UC
                    <button
                      @click.prevent="copyUcAddress"
                      class="copy_btn ml-2"
                      :class="{ copied_address: copiedAddress }"
                    >
                      <v-icon size="15" class="home_icon">mdi-home</v-icon>
                      Marcar como endereço residencial
                    </button>
                  </div>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <v-text-field
                    id="cep"
                    v-model="consumer_unit.address.postal_code"
                    label="CEP"
                    mask="#####-###"
                    :rules="[
                      (v) =>
                        v.length > 7 ||
                        'Ops. Parece que esse não é um CEP válido',
                    ]"
                    @change="buscaCep($event)"
                    :readonly="!canEdit(consumer_unit.status)"
                  >
                    <template v-slot:append>
                      <v-progress-circular
                        v-if="loading_cep"
                        indeterminate
                        color="primary"
                        size="18"
                      ></v-progress-circular>
                    </template>
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <v-autocomplete
                    :items="states"
                    item-text="name"
                    item-value="initials"
                    label="Estado"
                    prepend-icon="map"
                    v-model="consumer_unit.address.state"
                    return-object
                    :rules="[
                      (v) => !!v || 'Por favor, selecione o seu estado.',
                    ]"
                    hint="Selecione o estado"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="consumer_unit.address.city"
                    label="Cidade"
                    :rules="[
                      (v) =>
                        !!v || 'Por favor, determine a cidade do endereço.',
                    ]"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="consumer_unit.address.district"
                    label="Bairro"
                    :rules="[
                      (v) =>
                        !!v || 'Por favor, determine o bairro do endereço.',
                    ]"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-text-field>
                </v-flex>

                <v-flex xs4 sm3 md4>
                  <v-text-field
                    v-model="consumer_unit.address.address"
                    label="Logradouro"
                    :rules="[
                      (v) =>
                        !!v || 'Por favor, determine o logradouro do endereço.',
                    ]"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex xs4 sm3 md2>
                  <v-text-field
                    v-model="consumer_unit.address.address_number"
                    label="Número"
                    type="number"
                    :rules="[
                      (v) =>
                        !!v || 'Por favor, determine o número do endereço.',
                    ]"
                    required
                    :readonly="!canEdit(consumer_unit.status)"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 md2>
                  <v-text-field
                    v-model="consumer_unit.address.address_complement"
                    label="Complemento"
                    :readonly="!canEdit(consumer_unit.status)"
                  >
                  </v-text-field>
                </v-flex>

                <v-flex xs12 sm12 md12>
                  <div class="font-weight-bold">Histórico de consumo</div>
                </v-flex>
                <v-layout row wrap v-if="consumer_unit.history" class="px-1">
                  <v-flex
                    xs12
                    sm6
                    md3
                    v-for="(history, index) in consumer_unit.history.slice(
                      0,
                      4,
                    )"
                    :key="index"
                  >
                    <p>{{ history.mes_ano }}</p>
                    <v-text-field
                      v-model.number="history.consumo"
                      type="number"
                      prefix="kWh"
                      :rules="[
                        (v) => !!v || 'Por favor, insira o valor do consumo',
                      ]"
                      required
                      :readonly="!canEdit(consumer_unit.status)"
                      class="pt-0"
                    ></v-text-field>
                  </v-flex>
                  <v-flex
                    xs12
                    sm12
                    md12
                    v-if="history_alert_message"
                    class="py-0"
                  >
                    <p class="history_alert">
                      <v-icon size="20" color="orange" class="icon"
                        >mdi-alert-circle-outline</v-icon
                      >
                      {{ history_alert_message }}
                    </p>
                  </v-flex>
                </v-layout>
              </v-layout>

              <v-flex xs12 sm6 md3>
                <v-combobox
                  v-model="consumer_unit.comercial_status"
                  label="Cliente possui perfil comercial?"
                  required
                  :items="comercial_items"
                  item-text="text"
                  item-value="value"
                  :rules="[
                    (v) =>
                      !!v ||
                      'Por favor, selecione se o cliente possui perfil comercial ou não!',
                  ]"
                  :readonly="!canEdit(consumer_unit.status)"
                ></v-combobox>
              </v-flex>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end pb-3">
          <v-btn @click="closeDialog" color="error" max-width="50px"
            >fechar</v-btn
          >
          <v-btn
            @click="createQualification"
            color="primary"
            max-width="50px"
            :disabled="!canEdit(consumer_unit.status)"
            :loading="loading_qualification"
            ><v-icon left dark>rule</v-icon>Qualificar</v-btn
          >
        </v-card-actions>
      </v-card>

      <v-snackbar
        :color="snack.color"
        :bottom="snack.bottom"
        :top="snack.top"
        :left="snack.left"
        :right="snack.right"
        v-model="snack.snackbar"
        dark
      >
        <v-icon color="white" class="mr-3"> mdi-bell-plus </v-icon>
        <div>{{ snack.snackMessage }}</div>
        <v-icon size="16" @click="snack.snackbar = false">
          mdi-close-circle
        </v-icon>
      </v-snackbar>
    </v-container>
  </v-dialog>
</template>

<script>
import statesfile from "@/model/state";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { getCEP } from "../../../services/getCep";
import { initQualificationByForm } from "../../../services/qualification";
import { uploadData, uploadUcBilling } from "../../../services/sales";
import { uploadInvoice } from "../../../services/uploadInvoice";

export default {
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    qualification: {
      type: Object,
      required: false,
    },
    qualification_ids: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: "new",
    },
    isReSubscription: {
      type: Boolean,
      default: false,
    },
    selected_plans: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading_qualification: false,
      valid: false,
      availableFormats: ["jpg", "jpeg", "png", "pdf"],
      idPhoto: [
        {
          name: "",
          data: null,
        },
      ],
      snack: {
        color: null,
        top: true,
        bottom: false,
        left: false,
        right: false,
        snackMessage: "",
        snackbar: false,
      },
      verify_debits: false,
      verify_rules: true,
      loading_cep: false,
      states: statesfile.estados,
      rate_classes: ["Monofásico", "Bifásico", "Trifásico"],
      sub_classes: [
        "Comercial",
        "Outros Serviços e Outras Ativ",
        "Residencial",
      ],
      comercial_items: [
        { text: "Sim", value: true },
        { text: "Não", value: false },
      ],
      plans: [],
      installationNumberRule: [
        (v) => !!v || "Por favor, esse dado é obrigatório.",
        (v) => {
          switch (this.consumer_unit?.utility_company) {
            case "neoenergia-pe":
              if (v.length < 6 || v.length > 7)
                return "O número deve ter 6 ou 7 dígitos";
              break;

            case "neoenergia-ba":
              if (v.length < 6 || v.length > 8)
                return "O número deve ter de 6 a 8 dígitos";
              break;

            case "cpfl-piratininga":
              if (v.substr(0, 1) !== "2" && v.substr(0, 1) !== "4")
                return "O primeiro dígito deve ser 2 ou 4";
              if (v.substr(1, 1) !== "0") return "O segundo dígito deve ser 0";
              if (v.length !== 10) return "O número deve ter 10 dígitos";
              break;

            case "cemig":
              if (v.length !== 10) return "O número deve ter 10 dígitos";
              if (v.substr(0, 3) !== "300" && v.substr(0, 3) !== "301")
                return "O número deve começar com 300 ou 301";
              break;

            default:
              return true;
          }
          return true;
        },
        (v) => /^\d+$/.test(v) || "Deve conter apenas números.",
      ],
      customerNumberRule: [
        (v) => {
          if (this.consumer_unit?.utility_company === "cemig") {
            // Se for CEMIG, o campo é opcional
            return true;
          }
          return !!v || "Por favor, esse dado é obrigatório.";
        },
        (v) => {
          if (!v) return true; // Ignora validações adicionais se o campo estiver vazio e não for obrigatório
          switch (this.consumer_unit?.utility_company) {
            case "neoenergia-pe":
              if (v.length < 7) return "O número deve ter pelo menos 7 dígitos";
              break;

            case "neoenergia-ba":
              if (v.length < 6) return "O número deve ter pelo menos 6 dígitos";
              break;

            case "cpfl-piratininga":
              if (v.substr(0, 1) !== "7") return "O primeiro dígito deve ser 7";
              if (v.length !== 9) return "O número deve ter 9 dígitos";
              break;

            case "cemig":
              if (v && v.substr(0, 1) !== "7")
                return "O primeiro dígito deve ser 7";
              if (v && v.length !== 10) return "O número deve ter 10 dígitos";
              break;

            default:
              return true;
          }
          return true;
        },
        (v) => !v || /^\d+$/.test(v) || "Deve conter apenas números.",
      ],

      person_type_items: [
        { text: "Pessoa Física", value: "pf" },
        { text: "Pessoa Jurídica", value: "pj" },
      ],
      consumer_unit: {
        id: "",
        installation_number: "",
        customer_number: "",
        name: "",
        power_plant_id: "",
        percentage: 0,
        percentage_discount: 13,
        fare_amount: 0.95,
        user_id: "",
        label: "",
        rate_class: null,
        sub_class: null,
        comercial_status: null,
        average: 0,
        creditos: 0,
        mensalidade: 0,
        economia_mensal: 0,
        economia_anual: 0,
        economia_contrato: 0,
        status: "awaiting_contract_generation",
        address: {
          address: "",
          address_number: "",
          address_complement: "",
          district: "",
          postal_code: "",
          state: null,
          city: "",
          country: {
            name: "Brasil",
            initials: "BR",
          },
        },
        user: {
          customer_id: "",
          buyer_id: "",
          name: "",
          email: "",
          phone: "",
          cpf: "",
          group: "",
          payment_type: "",
          card_default: "",
        },
        powerplant: {
          power_plant_id: "",
          name: "",
          power_monthly: "",
        },
        history: [
          { mes_ano: "mês 1", consumo: null, dias: 30 },
          { mes_ano: "mês 2", consumo: null, dias: 30 },
          { mes_ano: "mês 3", consumo: null, dias: 30 },
          { mes_ano: "mês 4", consumo: null, dias: 30 },
        ],
        initial_average_comsumption: "",
        contract_id: null,
        cpf: "",
        person_type: "pf",
        company: "juntos",
        utility_company: null,
      },
      copiedAddress: false,
      nameRules: [
        (v) => !!v || "Por favor, digite o nome do titular da UC.",
        (v) => /.*\s.*/.test(v.trim()) || "Informe o nome completo.",
      ],
      history_alert_message: null,
      loading_parse_bill: false,
    };
  },

  computed: {
    ...mapState("sales", [
      "user",
      "lead",
      "consumer_unit_uploaded",
      "file",
      "subclasses",
    ]),
    ...mapState("configuration", ["utilityCompanies"]),
    ...mapGetters("sales", ["filteredPlans"]),
    ...mapGetters(["loggedUser"]),

    plan: {
      get() {
        return this.$store.state.sales.plan;
      },
      set(value) {
        this.$store.commit("sales/setPlan", value);
      },
    },
    customer_number_formatted() {
      if (this.consumer_unit.utility_company == "neoenergia-pe") {
        return "Código do Cliente";
      } else if (this.consumer_unit.utility_company == "cpfl-piratininga") {
        return "Conta Contrato";
      }
      return "Número do Cliente";
    },
    empty_consumer_unit() {
      return {
        id: "",
        installation_number: "",
        customer_number: "",
        name: "",
        power_plant_id: "",
        percentage: 0,
        percentage_discount: 13,
        fare_amount: 0.95,
        user_id: "",
        label: "",
        rate_class: null,
        sub_class: null,
        average: 0,
        creditos: 0,
        mensalidade: 0,
        economia_mensal: 0,
        economia_anual: 0,
        economia_contrato: 0,
        status: "awaiting_contract_generation",
        address: {
          address: "",
          address_number: "",
          address_complement: "",
          district: "",
          postal_code: "",
          state: null,
          city: "",
          country: {
            name: "Brasil",
            initials: "BR",
          },
        },
        user: {
          customer_id: "",
          buyer_id: "",
          name: "",
          email: "",
          phone: "",
          cpf: "",
          group: "",
          payment_type: "",
          card_default: "",
        },
        powerplant: {
          power_plant_id: "",
          name: "",
          power_monthly: "",
        },
        history: [
          { mes_ano: "mês 1", consumo: null, dias: 30 },
          { mes_ano: "mês 2", consumo: null, dias: 30 },
          { mes_ano: "mês 3", consumo: null, dias: 30 },
          { mes_ano: "mês 4", consumo: null, dias: 30 },
        ],
        initial_average_comsumption: "",
        contract_id: null,
        cpf: "",
        person_type: "pf",
        company: "juntos",
        utility_company: "cemig",
        agent_email: null,
      };
    },
    uc_type() {
      if (this.type === "edit") {
        return "Edição";
      }
      return "Cadastro";
    },
  },

  mounted() {
    if (this.utilityCompanies?.length <= 0) this.getUtilityCompanies();
    if (this.user) {
      this.consumer_unit.person_type = this?.user?.person_type;
      this.consumer_unit.utility_company = this?.user?.utility_company;
    }
    if (this.user.cpf) {
      this.consumer_unit.cpf = this.user.cpf;
    }
    if (this.user.name && this.user.person_type === "pf") {
      this.consumer_unit.name = this.user.name;
    }
    if (this.user.company_name && this.user.person_type === "pj") {
      this.consumer_unit.name = this.user.company_name;
    }
    if (this.qualification) {
      this.consumer_unit = { ...this.consumer_unit, ...this.qualification };
    }
    if (!this.consumer_unit.utility_company) {
      this.consumer_unit.utility_company = "cemig";
    }
    this.plans = this.selected_plans;

    this.agent_email = this.loggedUser.email || null;
  },

  watch: {
    consumer_unit_uploaded() {
      this.consumer_unit = JSON.parse(
        JSON.stringify(this.consumer_unit_uploaded),
      );
    },
    "consumer_unit.person_type"() {
      this.plans = this.filteredPlans({
        person_type: this.consumer_unit.person_type,
        company: this.consumer_unit.company,
        utility_company: this.consumer_unit.utility_company,
      });
    },
    "consumer_unit.utility_company"() {
      this.plans = this.filteredPlans({
        person_type: this.consumer_unit.person_type,
        company: this.consumer_unit.company,
        utility_company: this.consumer_unit.utility_company,
      });
      this.getSubClassByUtility(this.consumer_unit.utility_company);
    },
    "consumer_unit.history": {
      deep: true,
      handler() {
        this.checkHistoryReliability();
      },
    },
    "consumer_unit.sub_class"() {
      this.consumer_unit.fare_amount = this.consumer_unit.sub_class.value;
    },
  },

  methods: {
    ...mapActions("sales", [
      "getPlansByParams",
      "setConsumerUnitUploaded",
      "getSubClassByUtility",
    ]),
    ...mapActions("qualification", ["getQualifications"]),
    ...mapActions("configuration", ["getUtilityCompanies"]),
    ...mapMutations("sales", [
      "setPlan",
      "setFile",
      "setUserAddress",
      "resetUserAddress",
    ]),

    resetCustomerNumberErrors() {
      this.$refs.customerNumberField.resetValidation();
      this.consumer_unit.customer_number = "";
    },

    closeDialog() {
      this.consumer_unit = JSON.parse(JSON.stringify(this.empty_consumer_unit));
      this.$emit("closeDialog");
    },

    validaCPF(strCPF) {
      if (!strCPF) return "";
      let Soma;
      let Resto;
      Soma = 0;
      if (strCPF == "00000000000") return false;
      if (strCPF == "11111111111") return false;

      for (let i = 1; i <= 9; i++)
        Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
      Resto = (Soma * 10) % 11;

      if (Resto == 10 || Resto == 11) Resto = 0;
      if (Resto != parseInt(strCPF.substring(9, 10))) return false;

      Soma = 0;
      for (let i = 1; i <= 10; i++)
        Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (12 - i);
      Resto = (Soma * 10) % 11;

      if (Resto == 10 || Resto == 11) Resto = 0;
      return Resto == parseInt(strCPF.substring(10, 11));
    },

    validaCNPJ(strCNPJ) {
      if (!strCNPJ) return "";
      let cnpj = strCNPJ.replace(/[^\d]+/g, "");

      if (cnpj.length != 14) return false;

      if (
        cnpj == "00000000000000" ||
        cnpj == "11111111111111" ||
        cnpj == "22222222222222" ||
        cnpj == "33333333333333" ||
        cnpj == "44444444444444" ||
        cnpj == "55555555555555" ||
        cnpj == "66666666666666" ||
        cnpj == "77777777777777" ||
        cnpj == "88888888888888" ||
        cnpj == "99999999999999"
      )
        return false;

      let tamanho = cnpj.length - 2;
      let numeros = cnpj.substring(0, tamanho);
      let digitos = cnpj.substring(tamanho);
      let soma = 0;
      let pos = tamanho - 7;
      for (let i = tamanho; i >= 1; i--) {
        soma += numeros.charAt(tamanho - i) * pos--;
        if (pos < 2) pos = 9;
      }
      let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
      if (resultado != digitos.charAt(0)) return false;

      tamanho = tamanho + 1;
      numeros = cnpj.substring(0, tamanho);
      soma = 0;
      pos = tamanho - 7;
      for (let i = tamanho; i >= 1; i--) {
        soma += numeros.charAt(tamanho - i) * pos--;
        if (pos < 2) pos = 9;
      }
      resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
      return resultado == digitos.charAt(1);
    },

    buscaCep(cep) {
      if (cep && cep !== "") {
        this.loading_cep = true;
        getCEP(cep)
          .then((result) => {
            this.consumer_unit.address.address = result.data.logradouro;
            this.consumer_unit.address.district = result.data.bairro;
            this.consumer_unit.address.city = result.data.localidade;
            this.consumer_unit.address.state = this.states.find(
              (item) => item.initials === result.data.uf,
            );
            this.loading_cep = false;
            this.snack.snackbar = false;
          })
          .catch((error) => {
            console.error(error);
            this.loading_cep = false;
          });
      }
    },

    convertToTitleCase() {
      const prepositions = ["de", "da", "das", "do", "dos", "e"];
      this.consumer_unit.name = this.consumer_unit.name
        .toLowerCase()
        .replace(/\b[^ \t\n\r\f\v]+/g, (word) => {
          const lowerCasedWord = word.toLowerCase();
          return prepositions.includes(lowerCasedWord)
            ? lowerCasedWord
            : this.capitalize(word);
        });
    },

    capitalize(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },

    async createQualification() {
      this.loading_qualification = true;
      if (this.$refs.consumer_unit_form.validate()) {
        let consumptionHistory = this.consumer_unit.history.reduce(
          (prev, cur) => {
            return prev + cur.consumo;
          },
          0,
        );
        if (consumptionHistory < 1) {
          this.loading_qualification = false;
          this.show_snack(
            "Digite o consumo de pelo menos um mês do histórico.",
            "warning",
            "top",
          );
          return;
        }
        let form_data = {
          utility_company: this.consumer_unit.utility_company,
          name: this.consumer_unit.name,
          address: this.consumer_unit.address,
          cpf: this.consumer_unit.cpf,
          customer_number: this.consumer_unit.customer_number || "",
          consumer_unit: this.consumer_unit.installation_number,
          rate_class: this.consumer_unit.rate_class,
          sub_class: this.consumer_unit.sub_class.subclass
            ? this.consumer_unit.sub_class.subclass
            : this.consumer_unit.sub_class,
          fare_amount: this.consumer_unit.fare_amount,
          history: this.consumer_unit.history,
          verify_debits:
            this.consumer_unit.utility_company === "cemig"
              ? this.verify_debits
              : false,
          verify_rules: this.verify_rules,
        };
        await initQualificationByForm(
          this.consumer_unit.cpf,
          null,
          [this.consumer_unit.installation_number],
          [this.consumer_unit.customer_number],
          "inside_sales",
          form_data,
          this.consumer_unit.utility_company,
          null,
          this.agent_email,
          null,
        )
          .then(async (ret) => {
            if (ret.status === 200) {
              this.qualification_ids.push(...ret.data.ids);
              const params = {
                cpf: this.consumer_unit.cpf,
                qualification_ids: this.qualification_ids,
              };
              await this.getQualifications(params);
              this.setFile(this.idPhoto);
              await uploadUcBilling(ret.data.ids[0], this.idPhoto);
              this.consumer_unit = JSON.parse(
                JSON.stringify(this.empty_consumer_unit),
              );
              this.$emit("closeDialog");
            } else {
              this.show_snack(
                "Erro ao iniciar qualificação.",
                "warning",
                "top",
              );
            }
          })
          .catch((err) => {
            console.error(err);
            this.show_snack("Erro ao iniciar qualificação.", "warning", "top");
          });
      }
      this.loading_qualification = false;
    },

    formattedPlanText(item) {
      return item.name + " " + item.person_type + " - " + item.discount + "%";
    },

    show_snack(message, color, ...args) {
      this.snack.top = true;
      this.snack.bottom = false;
      this.snack.left = false;
      this.snack.right = false;

      for (const loc of args) {
        this[loc] = true;
      }

      this.snack.color = color;
      this.snack.snackMessage = message;
      this.snack.snackbar = true;
    },

    removeFile() {
      this.idPhoto = [
        {
          name: "",
          data: null,
        },
      ];
    },

    async uploadFile(userId, fileData) {
      const data = {
        id: userId,
        data: fileData,
      };
      await uploadData(data);
    },

    async pickedPhotos(event) {
      this.removeFile();
      const photosFiles = event.target.files;
      for (const photo of photosFiles) {
        if (photo !== undefined) {
          let extension = photo.name.split(".")[1].toLowerCase();
          if (!this.availableFormats.includes(extension)) {
            this.show_snack(
              "O arquivo deve ser PDF, PNG, JPG ou JPEG!",
              "error",
              "top",
            );
            return;
          }
          if (extension == "pdf") {
            this.detectFiles(photosFiles);
          }
          if (!this.idPhoto[0].name) {
            this.idPhoto = [];
          }
          this.idPhoto.push({
            name: photo.name,
            data: photo,
          });
        }
      }
    },

    pickIdPhoto() {
      this.$refs.idPhoto.click();
    },

    detectFiles(fileList) {
      this.loading_parse_bill = true;
      Array.from(Array(fileList.length).keys()).map((x) => {
        var formData = new FormData();
        formData.append("invoice", fileList[x]);
        formData.append("utility_company", this.consumer_unit.utility_company);
        this.uploadInv(formData)
          .then((res) => {
            let tarifa_energia = 0;
            let _history = [
              { mes_ano: "mês 1", consumo: 0, dias: 30 },
              { mes_ano: "mês 2", consumo: 0, dias: 30 },
              { mes_ano: "mês 3", consumo: 0, dias: 30 },
              { mes_ano: "mês 4", consumo: 0, dias: 30 },
            ];
            if (res.data.historico) {
              _history = res.data.historico.map((h) => {
                return {
                  mes_ano: h.competencia,
                  consumo: h.consumo,
                  dias: h.dias,
                };
              });
            }
            if (this.consumer_unit.utility_company === "cemig") {
              const energia_eletrica = res.data.valores_faturados.find((t) =>
                t.descricao.includes("Energia Elétrica"),
              );
              tarifa_energia = energia_eletrica?.tarifa_preco || 0;
            }
            // if (
            //   this.consumer_unit.utility_company === "neoenergia-pe" ||
            //   this.consumer_unit.utility_company === "neoenergia-ba" ||
            //   this.consumer_unit.utility_company === "cpfl-piratininga"
            // )
            else {
              const tusdRgx = /Consumo(\s*([A-Za-z]*\s*)*)(\[KWh\])*-TUSD/;
              const teRgx = /Consumo\s*-\s*TE/;
              const consumo_tusd = res.data.valores_faturados.find((t) =>
                tusdRgx.test(t.descricao),
              );
              const consumo_te = res.data.valores_faturados.find((t) =>
                teRgx.test(t.descricao),
              );
              tarifa_energia =
                consumo_tusd.tarifa_preco + consumo_te.tarifa_preco;
            }

            let street = res.data.endereco?.rua;
            let number = "";
            let complement = "";

            if (
              ["cemig", "neoenergia-pe"].includes(
                this.consumer_unit.utility_company,
              )
            ) {
              street = res.data.endereco?.rua?.match(/^([A-Z]+\s)+[A-Z]+/)[0];
              number = res.data.endereco?.rua?.match(/\d+/)[0];
              complement = res.data.endereco?.rua?.match(/\d+(.+)/)[1]?.trim();
            }

            if (street?.startsWith("ENDEREÇO: ")) {
              street = street?.slice("ENDEREÇO: ".length);
            }

            const uc = {
              status: "awaiting_contract_generation",
              installation_number: res.data.numero_instalacao,
              customer_number: res.data.numero_cliente,
              utility_company: this.consumer_unit.utility_company,
              fare_amount: tarifa_energia,
              rate_class: this.mapClasse(res.data.classe),
              sub_class: this.mapSubClasse(res.data.subclasse),
              history: _history,
              name: res.data.nome,
              average: 0,
              percentage: 0,
              percentage_discount: 13,
              creditos: 0,
              mensalidade: 0,
              economia_mensal: 0,
              economia_anual: 0,
              economia_contrato: 0,
              address: {
                address: street,
                address_number: number,
                address_complement: complement,
                district: res.data.endereco.bairro,
                postal_code: res.data.endereco.cep,
                state: this.states?.find(
                  (item) => item.initials === res.data.endereco.estado,
                ),
                city: res.data.endereco.cidade,
                country: {
                  name: "Brasil",
                  initials: "BR",
                },
              },
              user: {
                name: res.data.nome,
              },
            };

            if (res.data.cpf || res.data.cnpj) {
              uc.cpf = res.data?.cpf || res.data?.cnpj;
              uc.person_type = uc.cpf.length > 11 ? "pj" : "pf";
            }

            this.consumer_unit = { ...this.consumer_unit, ...uc };

            if (this.consumer_unit?.customer_number) {
              this.loading_parse_bill = false;
            }
          })
          .catch((error) => {
            this.loading_parse_bill = false;
            this.show_snack(
              "Erro ao preencher dados a partir da conta.",
              "error",
              "top",
            );
            console.error(error);
          });
      });
    },

    async uploadInv(formData) {
      return await uploadInvoice(formData);
    },

    mapClasse(value) {
      const _value = value.toLowerCase();
      if (_value.includes("monofásico")) return "Monofásico";
      if (_value.includes("bifásico")) return "Bifásico";
      if (_value.includes("trifásico")) return "Trifásico";
      return value;
    },

    mapSubClasse(value) {
      const _value = value.toLowerCase();
      const subclass = this.subclasses?.find((item) => (item.subclass = value));
      if (subclass) return subclass;
      if (_value.includes("comercial")) return "Comercial";
      if (_value.includes("residencial")) return "Residencial";
      return value;
    },

    canEdit(status) {
      let allowed_status = [
        "error",
        "bill_not_found",
        "pending",
        "awaiting_contract_generation",
        "canceled",
        "contract_not_signed",
      ];
      if (status == "qualified") return false;
      if (allowed_status.includes(status)) return true;
      return this.isReSubscription;
    },

    copyUcAddress() {
      if (this.copiedAddress) {
        this.copiedAddress = false;
        this.resetUserAddress();
        this.show_snack("Endereço da UC removido do Cliente.", "green", "top");
        return;
      }

      const address = JSON.parse(JSON.stringify(this.consumer_unit.address));

      if (
        address.address == "" ||
        address.address_number == "" ||
        address.city == "" ||
        address.district == "" ||
        address.postal_code == ""
      ) {
        this.show_snack("Preencha o endereço completo da UC.", "red", "top");
      } else {
        this.copiedAddress = true;
        this.setUserAddress(address);
        this.show_snack(
          "Endereço da UC copiado para o Cliente.",
          "green",
          "top",
        );
      }
    },
    preventNumberChange(event) {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    },
    checkHistoryReliability() {
      this.history_alert_message = null;
      const history = JSON.parse(
        JSON.stringify(this.consumer_unit.history),
      )?.slice(0, 4);
      const consumo = history?.map((item) => item.consumo);
      const orderedConsumo = consumo?.sort((a, b) => b - a);
      const division = orderedConsumo[0] / orderedConsumo[1];

      if (orderedConsumo[2] == "" || orderedConsumo[2] == null) {
        return;
      }

      if (orderedConsumo[0] >= 20000) {
        this.history_alert_message =
          "Atenção! Histórico com consumo muito alto. Por favor, verifique se os dados estão corretos.";
        return;
      }

      if (division >= 5) {
        this.history_alert_message =
          "Atenção! Variação muito grande de consumo. Por favor, verifique se os dados estão corretos.";
        return;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.v-dialog__content {
  backdrop-filter: brightness(60%);
}

::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.icon_position {
  position: absolute;
  transform: translate(-25px, -5px) !important;
  background: #fff !important;
  padding: 6px;
}

.copy_btn {
  min-width: fit-content;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid #000;
  transition: 0.4s;

  &:hover {
    background-color: #f8f8f8;
  }
}

.copied_address {
  border-color: #7d3dff;
  color: #7d3dff;

  .home_icon {
    color: #7e3dffe0;
  }
}

.history_alert {
  color: #eb8e02;

  .icon {
    transform: translateY(2px);
  }
}
</style>
